import { batch } from 'react-redux';
import i18next from 'i18next';

import { addWarning, addError, dismissAll } from 'actions/Error';
import {
  openModalPage,
  checkIfBlockedExchange,
  waitForCartCalculationToFinish,
} from 'actions/modalPage';
import { resetShoppingCart } from 'actions/ShoppingCart';
import { saveSalesDocument } from 'actions/sales';
import { modalPages } from 'constants/modalPage';
import { getSelectedPos } from 'reducers/PointsOfSale';
import {
  getProductsInShoppingCart,
  getSelectedOffersSelector,
} from 'reducers/ShoppingCart';
import {
  getCurrencyCode,
  getIsModuleEnabled,
  getSetting,
} from 'reducers/configs/settings';
import { getSelectedCustomerID } from 'reducers/customerSearch';
import { getLoggedInEmployeeID, getOfferModuleRights } from 'reducers/Login';
import * as api from 'services/ErplyAPI/sales';
import { getSelectedWarehouseID } from 'reducers/warehouses';
import { getIsAReturn } from 'reducers/sales';

export const saveAsOffer = () => async (dispatch, getState) => {
  const state = getState();

  const offerRightsModule = getOfferModuleRights(getState());
  if (offerRightsModule?.add === 0) {
    return dispatch(
      addError(i18next.t('alerts:noRightsToAddOffers'), {
        selfDismiss: 4e3,
        dismissible: true,
      }),
    );
  }

  const shoppingCartItems = getProductsInShoppingCart(state);
  const currentEmployee = getLoggedInEmployeeID(state);

  if (!shoppingCartItems.length) {
    return dispatch(
      addWarning(i18next.t('alerts:shoppingCartEmpty'), {
        dismissible: false,
        selfDismiss: true,
      }),
    );
  }

  try {
    await dispatch(waitForCartCalculationToFinish());
  } catch (error) {
    return null; // Timed out
  }

  const isBlockedExchange = await dispatch(checkIfBlockedExchange());
  if (isBlockedExchange) return null;

  const hasNegative = shoppingCartItems.some(o => o.amount < 0);
  if (hasNegative) {
    return dispatch(
      addWarning(
        i18next.t('gridButtons:alerts.returnSaveError', {
          context: 'offer',
        }),
        {
          selfDismiss: true,
          dismissible: false,
        },
      ),
    );
  }

  const isMultiCurrencyEnabled = getIsModuleEnabled('pos_multicurrency')(
    getState(),
  );

  const currencyCode = isMultiCurrencyEnabled
    ? await dispatch(
        openModalPage({
          component: modalPages.currencySelection,
          isPopup: true,
        }),
      )
    : getCurrencyCode(state);

  const requestParams = {
    currencyCode,
    customerID: getSelectedCustomerID(state),
    warehouseID: getSelectedPos(state).warehouseID,
    pointOfSaleID: getSelectedPos(state).pointOfSaleID,
    confirmed: 1,
    type: 'OFFER',
    employeeID: currentEmployee,
    ...shoppingCartItems,
  };

  try {
    dispatch(
      addWarning(i18next.t('alerts:saving.offer'), { dismissible: false }),
    );
    await dispatch(saveSalesDocument({ salesDocument: requestParams }));
    batch(() => {
      dispatch(resetShoppingCart());
      dispatch(dismissAll());
    });
  } catch (error) {
    batch(() => {
      dispatch(dismissAll());
      dispatch(
        addError(i18next.t('alerts:saving.offer_error'), {
          selfDismiss: true,
        }),
      );
    });
  }

  return true;
};

export const fetchOffers = () => async (dispatch, getState) => {
  const state = getState();

  const offerRightsModule = getOfferModuleRights(getState());
  if (offerRightsModule?.view === 0) {
    dispatch(
      addError(i18next.t('alerts:noRightsToViewOrders'), {
        selfDismiss: 4e3,
        dismissible: true,
      }),
    );
    return;
  }

  dispatch(
    addWarning(i18next.t('alerts:loading.offer'), { dismissible: false }),
  );

  const onlyCurrentLoc = getSetting(
    'touchpos_offers_only_from_current_location',
  )(state);

  const currentWarehouseID = getSelectedWarehouseID(state);
  const employeeID = getLoggedInEmployeeID(getState());

  const requestParams = {
    warehouseID: onlyCurrentLoc ? currentWarehouseID : undefined,
    employeeID:
      employeeID && offerRightsModule?.view === 1 ? employeeID : undefined,
    type: 'OFFER',
    confirmed: 1,
    getRowsForAllInvoices: 1,
    getUnfulfilledDocuments: 1,
    recordsOnPage: 100,
  };

  try {
    const offers = await api.getSalesDocuments(requestParams);
    batch(() => {
      dispatch(
        openModalPage({
          component: modalPages.offers,
          props: { fetchedOffers: offers },
        }),
      );
      dispatch(dismissAll());
    });
  } catch (err) {
    batch(() => {
      dispatch(dismissAll());
      dispatch(
        addError(i18next.t('alerts:loading.offer_error'), {
          selfDismiss: true,
        }),
      );
    });
  }
};

export const deletePaidOffers = () => async (dispatch, getState) => {
  const state = getState();
  const selectedOffers = getSelectedOffersSelector(state);

  selectedOffers.forEach(offerID => {
    return api.deleteSalesDocument(offerID);
  });
};
